import React from 'react';
import { X } from 'lucide-react';
import {
  applyActivityPresetClientSafe,
  applyActivityPresetStandard,
  applySnagsPresetClientSafe,
  applySnagsPresetStandard,
  getDefaultActivityPdfSettings,
  getDefaultSnagsPdfSettings,
} from '../utils/pdfExportSettings';

function rowStyle() {
  return {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    marginBottom: 10,
    fontSize: 14,
    color: 'var(--cc-text-secondary)',
    cursor: 'pointer',
    userSelect: 'none',
  };
}

function SectionTitle({ children }) {
  return (
    <div
      style={{
        fontSize: 12,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        color: 'var(--cc-text-muted)',
        marginTop: 16,
        marginBottom: 8,
      }}
    >
      {children}
    </div>
  );
}

function CheckboxRow({ checked, onChange, label, disabled, hint }) {
  return (
    <label style={{ ...rowStyle(), opacity: disabled ? 0.45 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        style={{ width: 18, height: 18, marginTop: 2, flexShrink: 0, cursor: disabled ? 'not-allowed' : 'pointer' }}
      />
      <span>
        {label}
        {hint ? (
          <span style={{ display: 'block', fontSize: 12, color: 'var(--branding-app-surface-panel-hint)', fontWeight: 400, marginTop: 2 }}>{hint}</span>
        ) : null}
      </span>
    </label>
  );
}

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {'activity' | 'snags'} props.variant
 * @param {object} props.value
 * @param {(v: object) => void} props.onChange
 * @param {boolean} props.companyLogoAvailable
 */
export default function PdfExportSettingsModal({ open, onClose, variant, value, onChange, companyLogoAvailable }) {
  if (!open) return null;

  const set = (key) => (v) => onChange({ ...value, [key]: v });

  const presetRow = (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
      <span style={{ fontSize: 13, color: 'var(--cc-text-muted)', marginRight: 4 }}>Presets:</span>
      <button
        type="button"
        onClick={() =>
          onChange(
            variant === 'activity'
              ? { ...applyActivityPresetStandard(), includeCompanyLogo: value.includeCompanyLogo }
              : { ...applySnagsPresetStandard(), includeCompanyLogo: value.includeCompanyLogo },
          )
        }
        style={{
          padding: '6px 12px',
          fontSize: 13,
          fontWeight: 600,
          borderRadius: 6,
          border: '1px solid var(--branding-app-surface-panel-border)',
          background: 'var(--cc-card-bg)',
          color: 'var(--cc-text-secondary)',
          cursor: 'pointer',
        }}
      >
        Standard
      </button>
      <button
        type="button"
        onClick={() =>
          onChange(
            variant === 'activity'
              ? { ...applyActivityPresetClientSafe(), includeCompanyLogo: value.includeCompanyLogo }
              : { ...applySnagsPresetClientSafe(), includeCompanyLogo: value.includeCompanyLogo },
          )
        }
        style={{
          padding: '6px 12px',
          fontSize: 13,
          fontWeight: 600,
          borderRadius: 6,
          border: '1px solid var(--branding-app-surface-panel-border)',
          background: 'var(--cc-card-bg)',
          color: 'var(--cc-text-secondary)',
          cursor: 'pointer',
        }}
        title="Hides person names where noted; keeps dates, teams, and most text."
      >
        Client-safe
      </button>
      <button
        type="button"
        onClick={() =>
          onChange(
            variant === 'activity'
              ? getDefaultActivityPdfSettings()
              : getDefaultSnagsPdfSettings(),
          )
        }
        style={{
          padding: '6px 12px',
          fontSize: 13,
          fontWeight: 600,
          borderRadius: 6,
          border: '1px solid var(--branding-app-surface-panel-border)',
          background: 'var(--branding-app-surface-panel)',
          color: 'var(--cc-text-muted)',
          cursor: 'pointer',
        }}
      >
        Reset all
      </button>
    </div>
  );

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="pdf-export-settings-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10020,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        background: 'rgba(15, 23, 42, 0.45)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 440,
          maxHeight: 'min(88vh, 720px)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--cc-card-bg)',
          borderRadius: 12,
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
          border: '1px solid var(--branding-app-surface-panel-border)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 18px',
            borderBottom: '1px solid var(--branding-app-surface-panel-border)',
            flexShrink: 0,
          }}
        >
          <h2 id="pdf-export-settings-title" style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--cc-text-primary)' }}>
            PDF export options
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              border: 'none',
              borderRadius: 8,
              background: 'var(--branding-app-surface)',
              color: 'var(--cc-text-secondary)',
              cursor: 'pointer',
            }}
          >
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: '12px 18px 8px', flexShrink: 0 }}>{presetRow}</div>

        <div style={{ overflowY: 'auto', padding: '4px 18px 18px', flex: 1 }}>
          {variant === 'activity' ? (
            <>
              <SectionTitle>Post</SectionTitle>
              <CheckboxRow label="Date & time" checked={value.includePostDateTime} onChange={set('includePostDateTime')} />
              <CheckboxRow label="Team name" checked={value.includeTeamName} onChange={set('includeTeamName')} />
              <CheckboxRow label="Person who posted" checked={value.includeAuthorName} onChange={set('includeAuthorName')} />
              <CheckboxRow label="Caption / comment on post" checked={value.includePostBody} onChange={set('includePostBody')} />
              <CheckboxRow
                label="Voice & video notes (stub text only)"
                checked={value.includeMediaNotes}
                onChange={set('includeMediaNotes')}
                hint="PDF does not embed audio or video files."
              />

              <SectionTitle>Comments</SectionTitle>
              <CheckboxRow
                label="Include comment thread"
                checked={value.includeCommentThread}
                onChange={set('includeCommentThread')}
              />
              <CheckboxRow
                label="Commenter names"
                checked={value.includeCommentAuthorNames}
                onChange={set('includeCommentAuthorNames')}
                disabled={!value.includeCommentThread}
              />
              <CheckboxRow
                label="Photos attached to comments"
                checked={value.includeCommentImages}
                onChange={set('includeCommentImages')}
                disabled={!value.includeCommentThread}
              />
            </>
          ) : (
            <>
              <SectionTitle>Issue</SectionTitle>
              <CheckboxRow
                label="Status, priority & type"
                checked={value.includeIssueLabels}
                onChange={set('includeIssueLabels')}
              />
              <CheckboxRow label="Description" checked={value.includeDescription} onChange={set('includeDescription')} />
              <CheckboxRow label="Photos on snag" checked={value.includeSnagPhotos} onChange={set('includeSnagPhotos')} />
              <CheckboxRow
                label="Snag voice note (stub text)"
                checked={value.includeSnagVoiceNote}
                onChange={set('includeSnagVoiceNote')}
              />

              <SectionTitle>Reporting & assignment</SectionTitle>
              <CheckboxRow label="Reporter name" checked={value.includeReporterName} onChange={set('includeReporterName')} />
              <CheckboxRow label="Reported time" checked={value.includeReportedTime} onChange={set('includeReportedTime')} />
              <CheckboxRow label="Assigned teams" checked={value.includeAssignedTeams} onChange={set('includeAssignedTeams')} />

              <SectionTitle>Resolution</SectionTitle>
              <CheckboxRow
                label="Show resolution block"
                checked={value.includeResolvedInfo}
                onChange={set('includeResolvedInfo')}
                hint="When the snag is marked resolved."
              />
              <CheckboxRow
                label="Resolver name"
                checked={value.includeResolverName}
                onChange={set('includeResolverName')}
                disabled={!value.includeResolvedInfo}
              />

              <SectionTitle>Site replies</SectionTitle>
              <CheckboxRow
                label="Include site replies"
                checked={value.includeSiteReplies}
                onChange={set('includeSiteReplies')}
              />
              <CheckboxRow
                label="Reply author names"
                checked={value.includeSiteReplyAuthorNames}
                onChange={set('includeSiteReplyAuthorNames')}
                disabled={!value.includeSiteReplies}
              />
              <CheckboxRow
                label="Reply photos"
                checked={value.includeSiteReplyImages}
                onChange={set('includeSiteReplyImages')}
                disabled={!value.includeSiteReplies}
              />
              <CheckboxRow
                label="Reply voice notes (stub text)"
                checked={value.includeReplyVoiceNotes}
                onChange={set('includeReplyVoiceNotes')}
                disabled={!value.includeSiteReplies}
              />

              <SectionTitle>PM notes</SectionTitle>
              <CheckboxRow label="Include PM notes" checked={value.includePmNotes} onChange={set('includePmNotes')} />
              <CheckboxRow
                label="PM name on note"
                checked={value.includePmAuthorNames}
                onChange={set('includePmAuthorNames')}
                disabled={!value.includePmNotes}
              />
              <CheckboxRow
                label="PM note photos"
                checked={value.includePmNoteImages}
                onChange={set('includePmNoteImages')}
                disabled={!value.includePmNotes}
              />
              <CheckboxRow
                label="PM voice notes (stub text)"
                checked={value.includePmVoiceNotes}
                onChange={set('includePmVoiceNotes')}
                disabled={!value.includePmNotes}
              />
            </>
          )}

          <SectionTitle>Branding</SectionTitle>
          <CheckboxRow
            label="Company logo on first page"
            checked={Boolean(value.includeCompanyLogo) && companyLogoAvailable}
            onChange={set('includeCompanyLogo')}
            disabled={!companyLogoAvailable}
            hint={
              companyLogoAvailable
                ? 'From Settings → branding.'
                : 'Upload a logo in Settings → branding to enable this.'
            }
          />
        </div>

        <div
          style={{
            padding: '12px 18px',
            borderTop: '1px solid var(--branding-app-surface-panel-border)',
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '8px 18px',
              fontSize: 14,
              fontWeight: 600,
              borderRadius: 8,
              border: 'none',
              background: 'var(--branding-app-primary, #2563eb)',
              color: 'var(--branding-app-primary-fg, #fff)',
              cursor: 'pointer',
            }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
